import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export type UserPlan = 'stats' | 'premium' | null;

// Routes accessibles selon le plan
const STATS_ROUTES = ['/statistics', '/subscription', '/settings', '/api-keys'];
const PREMIUM_ROUTES = [
  '/dashboard', 
  '/caisse', 
  '/retrait', 
  '/analytics', 
  '/retrait-analytics', 
  '/history', 
  '/team', 
  '/export-import'
];

interface PlanAccess {
  plan: UserPlan;
  loading: boolean;
  hasStatsAccess: boolean;
  hasPremiumAccess: boolean;
  canAccessCaisseRetrait: boolean;
  canAccessRoute: (route: string) => boolean;
  updatePlan: (newPlan: UserPlan) => Promise<boolean>;
}

export function usePlanAccess(): PlanAccess {
  const [plan, setPlan] = useState<UserPlan>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const loadPlan = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('subscription_plan')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error loading plan:', error);
          setPlan(null);
        } else {
          setPlan((data?.subscription_plan as UserPlan) || null);
        }
      } catch (error) {
        console.error('Error loading plan:', error);
        setPlan(null);
      } finally {
        setLoading(false);
      }
    };

    loadPlan();
  }, [user?.id]);

  const updatePlan = async (newPlan: UserPlan): Promise<boolean> => {
    if (!user?.id || !newPlan) return false;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ subscription_plan: newPlan })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating plan:', error);
        return false;
      }

      setPlan(newPlan);
      return true;
    } catch (error) {
      console.error('Error updating plan:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const canAccessRoute = (route: string): boolean => {
    if (!plan) return false;
    
    // Premium a accès à tout
    if (plan === 'premium') return true;
    
    // Stats a accès uniquement aux routes STATS_ROUTES
    if (plan === 'stats') {
      return STATS_ROUTES.some(allowedRoute => route.startsWith(allowedRoute));
    }
    
    return false;
  };

  return {
    plan,
    loading,
    hasStatsAccess: plan === 'stats' || plan === 'premium',
    hasPremiumAccess: plan === 'premium',
    canAccessCaisseRetrait: plan === 'premium',
    canAccessRoute,
    updatePlan,
  };
}
